import PropTypes from 'prop-types';
import React, { useMemo, useEffect, memo } from 'react';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { List } from 'immutable';
import { push } from 'connected-react18-router';

import config from '../../config';
import { showError } from '../../utils/MessagePopup';
import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { DirectorDashboard } from '../../components/DirectorDashboard';
import DirectorDashboardRouting from '../../routing/DirectorDashboardRouting';
import { AppHeader } from '../../components/AppHeader';
import { usePopupContext } from '../../contexts/PopupContext';

import style from './style.module.css';

const tabs = config.tabs.get('directorDashboard');

const hasTabAccess = (tab, userRole) => {
  const access = tab?.get('access');

  return !access || access.includes(userRole);
};

const DirectorDashboardContainer = ({ tabs, push, user, location }) => {
  const { openPopup, closePopup } = usePopupContext();
  const currentPathname = location?.pathname ?? '';
  const currentTab = tabs
    .filter(tab => {
      const tabPath = tab.get('to');

      return currentPathname === tabPath || currentPathname.startsWith(`${tabPath}/`);
    })
    .maxBy(tab => tab.get('to').length);
  const pageTitle = currentTab?.get('name') ?? 'Director Dashboard';
  const userRole = user?.getIn(['roles', 0, 'slug'], null);
  const hasAccess = Boolean(user) && hasTabAccess(currentTab, userRole);
  const availableTabs = useMemo(() => tabs.filter(tab => hasTabAccess(tab, userRole)), [tabs, userRole]);

  const kpiConsole = useMemo(
    () => (
      <div className={style.kpiConsole}>
        <div className={style.divider} />
        <div className={style.directorInfo}>
          <span className={style.title}>{user?.get('title')} Dashboard</span>
          <span className={style.subtitle}>{user?.get('moduleOwnerName')} Module</span>
        </div>
      </div>
    ),
    [user],
  );

  useEffect(() => {
    if (!hasAccess) {
      showError(openPopup, config.permisionError, () => {
        push('/');
        closePopup();
      });
    }
  }, [hasAccess, openPopup, closePopup, push]);

  if (!hasAccess) return null;

  return (
    <>
      <Helmet title={pageTitle} />
      <div className={style.wrapper}>
        <AppHeader>{kpiConsole}</AppHeader>
        <DirectorDashboard tabs={availableTabs}>
          <DirectorDashboardRouting />
        </DirectorDashboard>
      </div>
    </>
  );
};

DirectorDashboardContainer.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  push: PropTypes.func.isRequired,
  tabs: PropTypes.instanceOf(List).isRequired,
};

function mapStateToProps(state) {
  return {
    tabs,
    user: state.auth.get('user', null),
    location: state.router.location,
  };
}

export default connect(mapStateToProps, { push }, mergeProps, connectOptions)(memo(DirectorDashboardContainer));
